import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { AppState } from '../../reducers/root.reducer';
import AdminProjectAdminsService from '../services/AdminProjectAdminsService';
import ProjectAdminType from '../types/ProjectAdminType';
import UploadState from '../../types/UploadState';

const NAME = 'AdminProjectAdmins';

const getAdmins = createAsyncThunk(
    `${NAME}/getAdmins`,
    AdminProjectAdminsService.getAdmins,
);
const createAdmin = createAsyncThunk(
    `${NAME}/createAdmin`,
    AdminProjectAdminsService.createAdmin,
);
const updateAdmin = createAsyncThunk(
    `${NAME}/updateAdmin`,
    AdminProjectAdminsService.updateAdmin,
);

export type AdminProjectAdminsState = UploadState<ProjectAdminType[]>;

const initialState: AdminProjectAdminsState = {
    data: [],
    loading: false,
    uploading: false,
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getAdmins.pending, (state: AdminProjectAdminsState) => {
            state.loading = true;
        });
        builder.addCase(
            getAdmins.fulfilled,
            (state: AdminProjectAdminsState, action) => {
                state.data = action.payload.data;
                state.loading = false;
            },
        );
        builder.addCase(
            getAdmins.rejected,
            (state: AdminProjectAdminsState) => {
                state.loading = false;
            },
        );

        builder.addCase(
            createAdmin.fulfilled,
            (state: AdminProjectAdminsState, action) => {
                state.data.push(action.payload.data);
            },
        );

        builder.addCase(
            updateAdmin.fulfilled,
            (state: AdminProjectAdminsState, action) => {
                const index = state.data.findIndex(
                    (admin) => admin.id === action.meta.arg.adminId,
                );

                if (index >= 0) {
                    state.data[index] = action.payload.data;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(updateAdmin.pending, createAdmin.pending),
            (state: AdminProjectAdminsState) => {
                state.uploading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                updateAdmin.fulfilled,
                updateAdmin.rejected,
                createAdmin.fulfilled,
                createAdmin.rejected,
            ),
            (state: AdminProjectAdminsState) => {
                state.uploading = false;
            },
        );
    },
});

const state = (state: AppState): AdminProjectAdminsState => state.admin.admins;

const AdminProjectAdminsSlice = {
    reducer: slice.reducer,
    actions: {
        getAdmins,
        createAdmin,
        updateAdmin,
    },
    state: {
        all: createSelector(state, (state: AdminProjectAdminsState) => state),
    },
};

export default AdminProjectAdminsSlice;
